import { DOCUMENT, Injectable, inject, signal } from '@angular/core';

export type ColorScheme = 'auto' | 'light' | 'dark';

/** key เดียวกับ script ใน src/index.html ที่ใส่คลาสก่อนแอปบูต */
export const COLOR_SCHEME_STORAGE_KEY = 'patient-dentapop:color-scheme:v1';

/** คลาสเดียวกับ dentapop-front-end ที่ src/styles/_dark-mode.scss ใช้กลับสีทั้งหน้า */
export const DARK_CLASS = 'vt-dark';

const SCHEMES: readonly ColorScheme[] = ['auto', 'light', 'dark'];

/**
 * จัดการโหมดสว่าง/มืดของแอป
 * โหมด auto ติดตามการเปลี่ยน prefers-color-scheme ของระบบตลอดอายุแอป
 */
@Injectable({ providedIn: 'root' })
export class ColorSchemeService {
  private readonly document = inject(DOCUMENT);
  private readonly media =
    this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)') ?? null;

  private readonly _scheme = signal<ColorScheme>(readStoredScheme());
  readonly scheme = this._scheme.asReadonly();

  constructor() {
    this.media?.addEventListener('change', () => this.apply());
    this.apply();
  }

  setScheme(scheme: ColorScheme): void {
    this._scheme.set(scheme);
    try {
      localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, scheme);
    } catch {
      // บางเบราว์เซอร์ปิด storage (เช่น private mode) การสลับโหมดยังทำงานได้ตามปกติ
    }
    this.apply();
  }

  isDark(): boolean {
    const scheme = this._scheme();
    return scheme === 'dark' || (scheme === 'auto' && (this.media?.matches ?? false));
  }

  private apply(): void {
    this.document.documentElement.classList.toggle(DARK_CLASS, this.isDark());
  }
}

function readStoredScheme(): ColorScheme {
  try {
    const stored = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    return SCHEMES.includes(stored as ColorScheme) ? (stored as ColorScheme) : 'auto';
  } catch {
    return 'auto';
  }
}
